import React, { useState } from 'react';
import { AIRCRAFT_CONFIGS, AIRCRAFT_GROUPS, CLASS_OF_SERVICE } from '../constants';
import {
  validateFlightNumber,
  validateAirportCode,
  validateDuration,
  validateTailNumber,
  validateGate,
  validateSeat,
  sanitizeText,
} from '../utils/validation';

// Auto-detected field display component
const AutoDetectedField = ({ value, onClick }) => (
  <div
    onClick={onClick}
    style={{
      width: '100%',
      padding: '12px 14px',
      borderRadius: '8px',
      border: '1px solid rgba(34, 197, 94, 0.3)',
      background: 'rgba(34, 197, 94, 0.1)',
      color: '#ffffff',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    }}
  >
    <span>{value}</span>
    <span style={{
      fontSize: '10px',
      color: '#22C55E',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 6L9 17l-5-5"/>
      </svg>
      Auto-detected
    </span>
  </div>
);

const FlightForm = ({ flightData, onChange }) => {
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');

  // Check if date is within FlightAware's 10-day lookback limit
  const isDateTooOld = (dateStr) => {
    if (!dateStr) return false;
    const flightDate = new Date(dateStr);
    const today = new Date();
    const tenDaysAgo = new Date(today);
    tenDaysAgo.setDate(today.getDate() - 10);
    return flightDate < tenDaysAgo;
  };

  const isDateTooFarFuture = (dateStr) => {
    if (!dateStr) return false;
    const flightDate = new Date(dateStr);
    const today = new Date();
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(today.getDate() + 14);
    return flightDate > twoWeeksFromNow;
  };

  const canUseLookup = flightData.flightNumber && flightData.date &&
                       !isDateTooOld(flightData.date) &&
                       !isDateTooFarFuture(flightData.date);

  const handleChange = (field, value) => {
    let processedValue = value;

    // Validate and sanitize inputs based on field type
    switch (field) {
      case 'flightNumber':
        const flightResult = validateFlightNumber(value);
        processedValue = flightResult.sanitized;
        break;

      case 'departure':
      case 'arrival':
        const airportResult = validateAirportCode(value);
        processedValue = airportResult.sanitized;
        break;

      case 'duration':
        const durationResult = validateDuration(value);
        processedValue = durationResult.sanitized;
        break;

      case 'tailNumber':
        const tailResult = validateTailNumber(value);
        processedValue = tailResult.sanitized;
        break;

      case 'departureGate':
      case 'arrivalGate':
        const gateResult = validateGate(value);
        processedValue = gateResult.sanitized;
        break;

      case 'seat':
        const seatResult = validateSeat(value);
        processedValue = seatResult.sanitized;
        break;

      case 'captain':
      case 'firstOfficer':
      case 'cabinCrew':
        // Sanitize text inputs to prevent XSS
        processedValue = sanitizeText(value, 50);
        break;

      default:
        break;
    }

    const updated = { ...flightData, [field]: processedValue };

    // Auto-update seat configuration when aircraft changes
    if (field === 'aircraft' && AIRCRAFT_CONFIGS[value]) {
      updated.seatConfiguration = AIRCRAFT_CONFIGS[value].configs[0];
      // Reset auto-detected flag when user manually changes aircraft
      updated.configAutoDetected = false;
      updated.aircraftAutoDetected = false;
    }

    // Reset auto-detected flags when user manually changes fields
    if (field === 'seatConfiguration') {
      updated.configAutoDetected = false;
    }
    if (field === 'departure') {
      updated.departureAutoDetected = false;
    }
    if (field === 'arrival') {
      updated.arrivalAutoDetected = false;
    }
    if (field === 'departureTime') {
      updated.departureTimeAutoDetected = false;
    }
    if (field === 'arrivalTime') {
      updated.arrivalTimeAutoDetected = false;
    }
    if (field === 'duration') {
      updated.durationAutoDetected = false;
    }
    if (field === 'tailNumber') {
      updated.tailNumberAutoDetected = false;
    }
    if (field === 'departureGate') {
      updated.departureGateAutoDetected = false;
    }
    if (field === 'arrivalGate') {
      updated.arrivalGateAutoDetected = false;
    }

    onChange(updated);
  };

  const handleFlightLookup = async () => {
    if (!flightData.flightNumber || !flightData.date) {
      setLookupError('Enter flight number and date first');
      return;
    }

    setIsLookingUp(true);
    setLookupError('');

    try {
      const params = new URLSearchParams({
        flightNumber: flightData.flightNumber,
        date: flightData.date,
      });

      const response = await fetch(`/api/flight-lookup?${params}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to look up flight');
      }

      // Update form with fetched data
      const updated = { ...flightData };

      if (data.departure) {
        updated.departure = data.departure;
        updated.departureAutoDetected = true;
      }
      if (data.arrival) {
        updated.arrival = data.arrival;
        updated.arrivalAutoDetected = true;
      }
      if (data.departureTime) {
        updated.departureTime = data.departureTime;
        updated.departureTimeAutoDetected = true;
      }
      if (data.arrivalTime) {
        updated.arrivalTime = data.arrivalTime;
        updated.arrivalTimeAutoDetected = true;
      }
      if (data.duration) {
        updated.duration = data.duration;
        updated.durationAutoDetected = true;
      }
      if (data.tailNumber) {
        updated.tailNumber = data.tailNumber;
        updated.tailNumberAutoDetected = true;
      }
      if (data.departureGate) {
        updated.departureGate = data.departureGate;
        updated.departureGateAutoDetected = true;
      }
      if (data.arrivalGate) {
        updated.arrivalGate = data.arrivalGate;
        updated.arrivalGateAutoDetected = true;
      }

      // Handle aircraft type and auto-set configuration
      if (data.aircraft && AIRCRAFT_CONFIGS[data.aircraft]) {
        updated.aircraft = data.aircraft;
        updated.aircraftAutoDetected = true;

        // Special handling for 767-300ER with configHint
        if (data.aircraft === 'Boeing 767-300ER' && data.configHint) {
          const configs = AIRCRAFT_CONFIGS[data.aircraft].configs;
          if (data.configHint === 'high-j') {
            // Select High-J config (first one with "High-J" in name)
            updated.seatConfiguration = configs.find(c => c.includes('High-J')) || configs[0];
            updated.configAutoDetected = true;
          } else if (data.configHint === 'standard') {
            // Select Standard config
            updated.seatConfiguration = configs.find(c => c.includes('Standard')) || configs[1];
            updated.configAutoDetected = true;
          } else {
            updated.seatConfiguration = configs[0];
            updated.configAutoDetected = true;
          }
        } else {
          // For all other aircraft, auto-detect the configuration
          updated.seatConfiguration = AIRCRAFT_CONFIGS[data.aircraft].configs[0];
          updated.configAutoDetected = true;
        }
      }

      onChange(updated);
      setLookupError('');

    } catch (error) {
      console.error('Flight lookup error:', error);
      setLookupError(error.message);
    } finally {
      setIsLookingUp(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '11px',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '8px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  };

  const sectionStyle = {
    marginBottom: '24px',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  };

  return (
    <div>
      {/* Flight Info Section */}
      <div style={sectionStyle}>
        <h3 style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ fontSize: '16px' }}>✈️</span>
          Flight Information
        </h3>
        
        <div className="form-grid" style={gridStyle}>
          <div>
            <label style={labelStyle}>Flight Number</label>
            <input
              type="text"
              value={flightData.flightNumber}
              onChange={(e) => handleChange('flightNumber', e.target.value)}
              placeholder="UA 001"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Date</label>
            <input
              type="date"
              value={flightData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Date Warning Message */}
        {flightData.date && (isDateTooOld(flightData.date) || isDateTooFarFuture(flightData.date)) && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            borderRadius: '8px',
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            fontSize: '12px',
            color: '#fbbf24',
            lineHeight: '1.5',
          }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              ⚠️ Auto-lookup unavailable
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {isDateTooOld(flightData.date)
                ? 'FlightAware only provides data for flights within the last 10 days. Please enter flight details manually below.'
                : 'FlightAware only provides data for flights within the next 2 weeks. Please enter flight details manually below.'}
            </div>
          </div>
        )}

        {/* Lookup Button */}
        <div style={{ marginTop: '12px' }}>
          <button
            onClick={handleFlightLookup}
            disabled={isLookingUp || !canUseLookup}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(0, 51, 160, 0.5)',
              background: isLookingUp
                ? 'rgba(0, 51, 160, 0.2)'
                : canUseLookup
                ? 'linear-gradient(135deg, rgba(0, 51, 160, 0.3) 0%, rgba(0, 68, 148, 0.3) 100%)'
                : 'rgba(255, 255, 255, 0.05)',
              color: canUseLookup ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
              fontSize: '13px',
              fontWeight: '600',
              cursor: isLookingUp || !canUseLookup ? 'not-allowed' : 'pointer',
              opacity: !canUseLookup ? 0.6 : 1,
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {isLookingUp ? (
              <>
                <span style={{
                  display: 'inline-block',
                  animation: 'spin 1s linear infinite',
                }}>⏳</span>
                Looking up flight...
              </>
            ) : (
              <>
                <span>🔍</span>
                Auto-fill from FlightAware
              </>
            )}
          </button>
          {lookupError && (
            <p style={{
              color: '#ef4444',
              fontSize: '12px',
              marginTop: '8px',
              textAlign: 'center',
            }}>
              {lookupError}
            </p>
          )}
        </div>

        <div style={{ ...gridStyle, marginTop: '16px' }}>
          <div>
            <label style={labelStyle}>Departure Airport</label>
            {flightData.departureAutoDetected ? (
              <AutoDetectedField
                value={flightData.departure}
                onClick={() => handleChange('departure', flightData.departure)}
              />
            ) : (
              <input
                type="text"
                value={flightData.departure}
                onChange={(e) => handleChange('departure', e.target.value.toUpperCase())}
                placeholder="EWR"
                maxLength={4}
                style={inputStyle}
              />
            )}
          </div>
          <div>
            <label style={labelStyle}>Arrival Airport</label>
            {flightData.arrivalAutoDetected ? (
              <AutoDetectedField
                value={flightData.arrival}
                onClick={() => handleChange('arrival', flightData.arrival)}
              />
            ) : (
              <input
                type="text"
                value={flightData.arrival}
                onChange={(e) => handleChange('arrival', e.target.value.toUpperCase())}
                placeholder="SFO"
                maxLength={4}
                style={inputStyle}
              />
            )}
          </div>
        </div>

        <div style={{ ...gridStyle, marginTop: '16px' }}>
          <div>
            <label style={labelStyle}>Departure Time</label>
            {flightData.departureTimeAutoDetected ? (
              <AutoDetectedField
                value={flightData.departureTime}
                onClick={() => handleChange('departureTime', flightData.departureTime)}
              />
            ) : (
              <input
                type="time"
                value={flightData.departureTime}
                onChange={(e) => handleChange('departureTime', e.target.value)}
                style={inputStyle}
              />
            )}
          </div>
          <div>
            <label style={labelStyle}>Arrival Time</label>
            {flightData.arrivalTimeAutoDetected ? (
              <AutoDetectedField
                value={flightData.arrivalTime}
                onClick={() => handleChange('arrivalTime', flightData.arrivalTime)}
              />
            ) : (
              <input
                type="time"
                value={flightData.arrivalTime}
                onChange={(e) => handleChange('arrivalTime', e.target.value)}
                style={inputStyle}
              />
            )}
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={labelStyle}>Duration</label>
          {flightData.durationAutoDetected ? (
            <AutoDetectedField
              value={flightData.duration}
              onClick={() => handleChange('duration', flightData.duration)}
            />
          ) : (
            <input
              type="text"
              value={flightData.duration}
              onChange={(e) => handleChange('duration', e.target.value)}
              placeholder="5h 25m"
              style={inputStyle}
            />
          )}
        </div>
      </div>

      {/* Aircraft Section */}
      <div style={sectionStyle}>
        <h3 style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ fontSize: '16px' }}>🛩️</span>
          Aircraft Details
        </h3>

        <div className="form-grid" style={gridStyle}>
          <div>
            <label style={labelStyle}>Aircraft Type</label>
            {flightData.aircraftAutoDetected ? (
              <AutoDetectedField
                value={flightData.aircraft}
                onClick={() => handleChange('aircraft', flightData.aircraft)}
              />
            ) : (
              <select
                value={flightData.aircraft}
                onChange={(e) => handleChange('aircraft', e.target.value)}
                style={inputStyle}
              >
                {AIRCRAFT_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.aircraft.map((aircraft) => (
                      <option key={aircraft} value={aircraft}>
                        {aircraft}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
          </div>
          <div>
            <label style={labelStyle}>Tail Number</label>
            {flightData.tailNumberAutoDetected ? (
              <AutoDetectedField
                value={flightData.tailNumber}
                onClick={() => handleChange('tailNumber', flightData.tailNumber)}
              />
            ) : (
              <input
                type="text"
                value={flightData.tailNumber}
                onChange={(e) => handleChange('tailNumber', e.target.value.toUpperCase())}
                placeholder="N12010"
                style={inputStyle}
              />
            )}
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={labelStyle}>Seat Configuration</label>
          {/* Show read-only field when config was auto-detected */}
          {flightData.configAutoDetected ? (
            <div style={{
              ...inputStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'rgba(34, 197, 94, 0.1)',
              borderColor: 'rgba(34, 197, 94, 0.3)',
            }}>
              <span>{flightData.seatConfiguration}</span>
              <span style={{
                fontSize: '10px',
                color: '#22C55E',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                Auto-detected
              </span>
            </div>
          ) : (
            <select
              value={flightData.seatConfiguration}
              onChange={(e) => handleChange('seatConfiguration', e.target.value)}
              style={inputStyle}
            >
              {AIRCRAFT_CONFIGS[flightData.aircraft]?.configs.map((config, idx) => (
                <option key={idx} value={config}>
                  {config}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Seat Section */}
      <div style={sectionStyle}>
        <h3 style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ fontSize: '16px' }}>💺</span>
          Your Seat
        </h3>

        <div className="form-grid" style={gridStyle}>
          <div>
            <label style={labelStyle}>Seat Assignment</label>
            <input
              type="text"
              value={flightData.seat}
              onChange={(e) => handleChange('seat', e.target.value.toUpperCase())}
              placeholder="1A"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Class of Service</label>
            <select
              value={flightData.classOfService}
              onChange={(e) => handleChange('classOfService', e.target.value)}
              style={inputStyle}
            >
              {CLASS_OF_SERVICE.map((cls) => (
                <option key={cls.value} value={cls.value}>
                  {cls.value}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Crew Section */}
      <div style={sectionStyle}>
        <h3 style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ fontSize: '16px' }}>👨‍✈️</span>
          Flight Crew
          <span style={{
            fontSize: '10px',
            fontWeight: '400',
            color: 'rgba(255,255,255,0.4)',
            marginLeft: '8px',
          }}>
            Optional
          </span>
        </h3>

        <div className="form-grid" style={gridStyle}>
          <div>
            <label style={labelStyle}>Captain</label>
            <input
              type="text"
              value={flightData.captain}
              onChange={(e) => handleChange('captain', e.target.value)}
              placeholder="Leave blank if unknown"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>First Officer</label>
            <input
              type="text"
              value={flightData.firstOfficer}
              onChange={(e) => handleChange('firstOfficer', e.target.value)}
              placeholder="Leave blank if unknown"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={labelStyle}>Cabin Crew</label>
          <input
            type="text"
            value={flightData.cabinCrew}
            onChange={(e) => handleChange('cabinCrew', e.target.value)}
            placeholder="Leave blank if unknown"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Gate Section */}
      <div style={sectionStyle}>
        <h3 style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ fontSize: '16px' }}>🚪</span>
          Gate Information
          <span style={{
            fontSize: '10px',
            fontWeight: '400',
            color: 'rgba(255,255,255,0.4)',
            marginLeft: '8px',
          }}>
            Optional
          </span>
        </h3>

        <div className="form-grid" style={gridStyle}>
          <div>
            <label style={labelStyle}>Departure Gate</label>
            {flightData.departureGateAutoDetected ? (
              <AutoDetectedField
                value={flightData.departureGate}
                onClick={() => handleChange('departureGate', flightData.departureGate)}
              />
            ) : (
              <input
                type="text"
                value={flightData.departureGate || ''}
                onChange={(e) => handleChange('departureGate', e.target.value.toUpperCase())}
                placeholder="C120"
                style={inputStyle}
              />
            )}
          </div>
          <div>
            <label style={labelStyle}>Arrival Gate</label>
            {flightData.arrivalGateAutoDetected ? (
              <AutoDetectedField
                value={flightData.arrivalGate}
                onClick={() => handleChange('arrivalGate', flightData.arrivalGate)}
              />
            ) : (
              <input
                type="text"
                value={flightData.arrivalGate || ''}
                onChange={(e) => handleChange('arrivalGate', e.target.value.toUpperCase())}
                placeholder="G10"
                style={inputStyle}
              />
            )}
          </div>
        </div>
      </div>

      {/* Lounge Add-ons Section */}
      <div style={sectionStyle}>
        <h3 style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ fontSize: '16px' }}>🍸</span>
          Lounge Add-ons
          <span style={{
            fontSize: '10px',
            fontWeight: '400',
            color: 'rgba(255,255,255,0.4)',
            marginLeft: '8px',
          }}>
            Optional
          </span>
        </h3>

        {/* Departure Lounges */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ ...labelStyle, marginBottom: '12px' }}>
            Departure ({flightData.departure || '---'})
          </label>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '10px 16px',
              borderRadius: '8px',
              border: `1px solid ${flightData.lounges?.departurePolarisLounge ? '#0033A0' : 'rgba(255,255,255,0.12)'}`,
              background: flightData.lounges?.departurePolarisLounge ? 'rgba(0, 51, 160, 0.2)' : 'rgba(255,255,255,0.04)',
              transition: 'all 0.2s ease',
            }}>
              <input
                type="checkbox"
                checked={flightData.lounges?.departurePolarisLounge || false}
                onChange={(e) => handleChange('lounges', {
                  ...flightData.lounges,
                  departurePolarisLounge: e.target.checked,
                })}
                style={{ display: 'none' }}
              />
              <span style={{
                width: '18px',
                height: '18px',
                borderRadius: '4px',
                border: `2px solid ${flightData.lounges?.departurePolarisLounge ? '#0033A0' : 'rgba(255,255,255,0.3)'}`,
                background: flightData.lounges?.departurePolarisLounge ? '#0033A0' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}>
                {flightData.lounges?.departurePolarisLounge && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <span style={{ fontSize: '13px', color: '#ffffff', fontWeight: '500' }}>
                ✨ Polaris Lounge
              </span>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '10px 16px',
              borderRadius: '8px',
              border: `1px solid ${flightData.lounges?.departureUnitedClub ? '#0066FF' : 'rgba(255,255,255,0.12)'}`,
              background: flightData.lounges?.departureUnitedClub ? 'rgba(0, 102, 255, 0.2)' : 'rgba(255,255,255,0.04)',
              transition: 'all 0.2s ease',
            }}>
              <input
                type="checkbox"
                checked={flightData.lounges?.departureUnitedClub || false}
                onChange={(e) => handleChange('lounges', {
                  ...flightData.lounges,
                  departureUnitedClub: e.target.checked,
                })}
                style={{ display: 'none' }}
              />
              <span style={{
                width: '18px',
                height: '18px',
                borderRadius: '4px',
                border: `2px solid ${flightData.lounges?.departureUnitedClub ? '#0066FF' : 'rgba(255,255,255,0.3)'}`,
                background: flightData.lounges?.departureUnitedClub ? '#0066FF' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}>
                {flightData.lounges?.departureUnitedClub && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <span style={{ fontSize: '13px', color: '#ffffff', fontWeight: '500' }}>
                🎫 United Club
              </span>
            </label>
          </div>
        </div>

        {/* Arrival Lounges */}
        <div>
          <label style={{ ...labelStyle, marginBottom: '12px' }}>
            Arrival ({flightData.arrival || '---'})
          </label>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '10px 16px',
              borderRadius: '8px',
              border: `1px solid ${flightData.lounges?.arrivalPolarisLounge ? '#0033A0' : 'rgba(255,255,255,0.12)'}`,
              background: flightData.lounges?.arrivalPolarisLounge ? 'rgba(0, 51, 160, 0.2)' : 'rgba(255,255,255,0.04)',
              transition: 'all 0.2s ease',
            }}>
              <input
                type="checkbox"
                checked={flightData.lounges?.arrivalPolarisLounge || false}
                onChange={(e) => handleChange('lounges', {
                  ...flightData.lounges,
                  arrivalPolarisLounge: e.target.checked,
                })}
                style={{ display: 'none' }}
              />
              <span style={{
                width: '18px',
                height: '18px',
                borderRadius: '4px',
                border: `2px solid ${flightData.lounges?.arrivalPolarisLounge ? '#0033A0' : 'rgba(255,255,255,0.3)'}`,
                background: flightData.lounges?.arrivalPolarisLounge ? '#0033A0' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}>
                {flightData.lounges?.arrivalPolarisLounge && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <span style={{ fontSize: '13px', color: '#ffffff', fontWeight: '500' }}>
                ✨ Polaris Lounge
              </span>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '10px 16px',
              borderRadius: '8px',
              border: `1px solid ${flightData.lounges?.arrivalUnitedClub ? '#0066FF' : 'rgba(255,255,255,0.12)'}`,
              background: flightData.lounges?.arrivalUnitedClub ? 'rgba(0, 102, 255, 0.2)' : 'rgba(255,255,255,0.04)',
              transition: 'all 0.2s ease',
            }}>
              <input
                type="checkbox"
                checked={flightData.lounges?.arrivalUnitedClub || false}
                onChange={(e) => handleChange('lounges', {
                  ...flightData.lounges,
                  arrivalUnitedClub: e.target.checked,
                })}
                style={{ display: 'none' }}
              />
              <span style={{
                width: '18px',
                height: '18px',
                borderRadius: '4px',
                border: `2px solid ${flightData.lounges?.arrivalUnitedClub ? '#0066FF' : 'rgba(255,255,255,0.3)'}`,
                background: flightData.lounges?.arrivalUnitedClub ? '#0066FF' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}>
                {flightData.lounges?.arrivalUnitedClub && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <span style={{ fontSize: '13px', color: '#ffffff', fontWeight: '500' }}>
                🎫 United Club
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightForm;
